import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BlockConfirmModal } from 'components/common/BlockConfirmModal';
import { BlockRepository } from 'repository/blockRepository';
import { ReportRepository } from 'repository/reportRepository';
import { useAlert } from 'contexts/AlertContext';
import styles from './MemberActionMenu.module.css';

/** 클래스 이름을 해시된 이름으로 바꿉니다. 모르는 이름은 그대로 둡니다.
 *  이름을 바꾸지 않고 스코프만 씌우려고 둔 얇은 헬퍼입니다. */
const cls = (names?: string): string =>
    (names || '').split(' ').filter(Boolean)
        .map((n) => (styles as Record<string, string>)[n] || n).join(' ');

interface MemberActionMenuProps {
    /** 대상 사용자 번호 */
    targetUserNo: number;
    /** 대상 표시명 (모달/알림용) */
    targetName: string;
    /** 차단 완료 시 콜백 — 부모가 목록 갱신 등에 활용 */
    onBlocked?: () => void;
}

/**
 * 모임 멤버 카드 우측 ⋮ 메뉴.
 * - "차단" 항목만 노출 (Phase 1 범위).
 * - 게스트(userNo < 0) 나 본인일 때 부모가 렌더하지 않도록 조건 처리.
 */
export const MemberActionMenu: React.FC<MemberActionMenuProps> = ({
    targetUserNo, targetName, onBlocked,
}) => {
    const [open, setOpen] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const { t } = useTranslation();
    const { showAlert } = useAlert();

    useEffect(() => {
        if (!open) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    const handleBlockClick = () => {
        setOpen(false);
        setConfirmOpen(true);
    };

    const handleConfirm = async ({ reason, report }: { reason?: string; report: boolean }) => {
        try {
            await BlockRepository.onPostBlock(targetUserNo, reason);
            // 인앱 알림 배너 등 다른 컴포넌트에 차단 변경 알림
            window.dispatchEvent(new CustomEvent('dokocrew:blockChange', {
                detail: { userNo: targetUserNo },
            }));
            // 신고 체크 시 — 차단과 별개로 적재 (실패해도 차단은 유지)
            if (report) {
                try {
                    await ReportRepository.onPostReport({
                        reportedUserNo: targetUserNo,
                        reason,
                    });
                } catch (re) {
                    console.warn('신고 적재 실패', re);
                }
            }
            showAlert({ message: report
                ? `${targetName}님을 차단하고 신고했습니다.`
                : `${targetName}님을 차단했습니다.` });
            onBlocked?.();
        } catch (e: any) {
            console.warn('차단 실패', e);
            const msg = e?.response?.data?.message || t('member.blockFail');
            showAlert({ message: msg });
        }
    };

    return (
        <>
            <div className={cls("member-action-menu")} ref={menuRef}>
                <button
                    type="button"
                    className={cls("member-action-menu-btn")}
                    aria-label={t('member.more')}
                    onClick={(e) => {
                        e.stopPropagation();
                        setOpen(prev => !prev);
                    }}
                >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <circle cx="8" cy="3" r="1.4" fill="#666" />
                        <circle cx="8" cy="8" r="1.4" fill="#666" />
                        <circle cx="8" cy="13" r="1.4" fill="#666" />
                    </svg>
                </button>
                {open && (
                    <div className={cls("member-action-menu-popover")}>
                        <button
                            type="button"
                            className={cls("member-action-menu-item member-action-menu-item-danger")}
                            onClick={handleBlockClick}
                        >
                            차단하기
                        </button>
                    </div>
                )}
            </div>
            <BlockConfirmModal
                isOpen={confirmOpen}
                targetName={targetName}
                onClose={() => setConfirmOpen(false)}
                onConfirm={handleConfirm}
            />
        </>
    );
};
